import {
  CategorizationTag,
  CategorizationTagColor,
  EmptyState,
  ExpandableContainer,
  Hypertext,
  HypertextSize,
  IconName,
  IconVariant,
  PageContainer,
  PageContainerSize,
  Panel,
  Popover,
  stringCompare,
  Title2,
  useClearSearchFilter,
  useDocumentTitle,
  useHasAccess,
  useSearchRegExps
} from "@quortex/front-common"
import { type MakeGenerics, useNavigate, useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { isEmpty } from "lodash-es"
import { type FC, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import AddOttEndpointModal from "src/App/play/components/wired/modals/AddOttEndpointModal"
import { getOttEndpointOptions } from "src/App/play/helpers/ottendpoints"
import illustrationNoOttEndpoints from "src/App/play/images/illustration-no-ott-endpoints.svg"
import { useInputList } from "src/queries/playInputs"
import { useOttEndpointList } from "src/queries/playOttEndpoints"
import { usePool } from "src/queries/playPools"
import {
  useEndpointFilterListCount,
  weightedStatuses
} from "@/App/play/App/Main/Pool/endpoints/helpers"
import { filterOttEndpointsBySearch } from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/helpers"
import OttEndpointContent from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/OttEndpointContent"
import OttEndpointFiltersSection from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/OttEndpointFiltersSection"
import OttEndpointInputHeader from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/OttEndpointInputHeader"
import {
  formatStreamingRegionLabels,
  getViewersRestrictionLabel
} from "@/App/play/App/Wizard/cloudsettings/CloudSettings/helpers"
import EmptySearchResult from "@/App/play/components/EmptySearchResult/EmptySearchResult"
import OttEndpointWizardStep from "@/App/play/components/features/endpoints/ottEndpoints/OttEndpointWizardStep/OttEndpointWizardStep"
import ShareUrlModal from "@/App/play/components/wired/modals/ShareUrlModal"
import { longRefetchOptions } from "@/App/play/helpers/input"
import { usePoolPath } from "@/App/play/hooks/navigation"
import { useProcessingList } from "@/queries/playProcessings"
import { useTargetList } from "@/queries/playTargets"
import { defaultPoolFilterList, useFilterStore } from "@/stores/filters"
import type { OttEndpointRow } from "./types"

type ExpandGenerics = MakeGenerics<{ Search: { openInputs?: string[] } }>
type FilterSearchGenerics = MakeGenerics<{
  Search: { statusFilterList?: api.ott.OttEndpoint["status"][]; search?: string }
}>

interface Props {
  isWizard?: boolean
}

const OttEndpointList: FC<Props> = ({ isWizard }) => {
  const { data: pool } = useSuspenseQuery({ ...usePool(), ...longRefetchOptions })
  const { data: processingList } = useSuspenseQuery({
    ...useProcessingList(),
    select: data => data.results
  })
  const { data: targetList } = useSuspenseQuery({
    ...useTargetList(),
    select: data => data.results
  })

  const poolPath = usePoolPath()
  useDocumentTitle(`OTT endpoints - ${pool.name}`)
  const { data: inputList } = useSuspenseQuery({
    ...useInputList(),
    ...longRefetchOptions,
    select: d => d?.results
  })

  const { search, statusFilterList, openInputs } = useSearch<
    FilterSearchGenerics & ExpandGenerics
  >()
  const ottEndpointFilterList = useFilterStore(
    state =>
      state.poolFilterList[pool.uuid]?.ottEndpointFilterList ??
      defaultPoolFilterList.ottEndpointFilterList
  )
  const setOttEndpointFilterList = useFilterStore(state => state.setOttEndpointFilterList)
  const form = useForm({
    defaultValues: {
      search: `${search ?? ""}`,
      statusFilterList: statusFilterList ?? ottEndpointFilterList.status
    }
  })
  const clearSearchFilter = useClearSearchFilter(form)
  const searchValue = form.watch("search")
  const searchRegExps = useSearchRegExps(searchValue)

  const navigate = useNavigate()
  const hasWriteAccess = useHasAccess(["owner", "editor"])

  const { data: ottEndpoints } = useSuspenseQuery({
    ...useOttEndpointList(),
    refetchInterval: 5_000,
    select: data => data.results
  })

  const rows: OttEndpointRow[] = useMemo(
    () =>
      (inputList ?? []).map(input => {
        const filteredEndpoints = ottEndpoints
          .filter(endpoint => endpoint.inputUuid === input.uuid)
          .map(endpoint => ({
            ...endpoint,
            input,
            processing: processingList.find(
              processing => processing.uuid === endpoint.processingUuid
            ),
            target: targetList.find(target => target.uuid === endpoint.targetUuid)
          }))
          .toSorted((a, b) => stringCompare(a.pretty || a.customPath, b.pretty || b.customPath))

        return {
          canAddOttEndpoint:
            getOttEndpointOptions(ottEndpoints, input.uuid, processingList, targetList).length > 0,
          endpoints: filteredEndpoints,
          endpointsCount: filteredEndpoints.length,
          inputName: input.name ?? "",
          inputUuid: input.uuid,
          status: weightedStatuses.find(
            status => !!filteredEndpoints.find(e => e.status === status)
          )
        }
      }),
    [inputList, ottEndpoints, processingList, targetList]
  )

  const statusFilterListValue = form.watch("statusFilterList")
  const isSearchOrFilterActive = !isEmpty(searchValue) || !isEmpty(statusFilterListValue)
  const { rowStatusCount } = useEndpointFilterListCount(rows, searchValue, (rowList, regexps) =>
    filterOttEndpointsBySearch(rowList as OttEndpointRow[], regexps)
  )

  const filteredRows = useMemo(() => {
    const statusFilteredRows = rows.filter(
      row => statusFilterListValue?.includes(row.status || "") || isEmpty(statusFilterListValue)
    )

    if (searchRegExps.length === 0) return statusFilteredRows

    return filterOttEndpointsBySearch(statusFilteredRows, searchRegExps)
  }, [searchRegExps, rows, statusFilterListValue])

  const sortedRows = useMemo(
    () => filteredRows.toSorted((a, b) => stringCompare(a.inputName, b.inputName)),
    [filteredRows]
  )

  const isSearchResultEmpty = isSearchOrFilterActive && sortedRows.length === 0
  const hasRows = sortedRows.length > 0
  const showMetrics = hasRows || isSearchResultEmpty
  const showEmptySearch = isEmpty(sortedRows) && isSearchOrFilterActive

  const descriptionText = isWizard
    ? "Finish by adding endpoints to make your content available through OTT."
    : "OTT players will fetch streams using these endpoints."

  const clearFilters = () => {
    setOttEndpointFilterList(pool.uuid, { status: [] })
    clearSearchFilter({ statusFilterList: [] })
  }

  if (isEmpty(inputList)) {
    return (
      <PageContainer size={PageContainerSize.XL}>
        <Panel>
          <Title2 className="mb-sm">OTT endpoints</Title2>
          <p className="mb-lg text-grey-600 text-sm">
            OTT players will fetch streams using these endpoints
          </p>
          <EmptyState
            description="To configure an OTT endpoint, first add an input"
            img={illustrationNoOttEndpoints}
          />
        </Panel>
      </PageContainer>
    )
  }

  return (
    <PageContainer size={PageContainerSize.XL}>
      <FormProvider {...form}>
        <Panel>
          <div className="flex items-center gap-sm">
            <Title2>OTT endpoints</Title2>
            <Popover
              body={
                <div className="flex flex-col gap-xs">
                  <span className="pt-2xs text-sm">
                    {formatStreamingRegionLabels(pool.streamingCountries)}
                  </span>
                  {!isWizard && (
                    <Hypertext
                      key={pool.uuid}
                      size={HypertextSize.SMALL}
                      to={`${poolPath}/settings/poolLocation`}
                    >
                      Configure
                    </Hypertext>
                  )}
                </div>
              }
              title="Viewers georestriction"
            >
              <CategorizationTag
                className="cursor-default"
                color={CategorizationTagColor.OUTLINE}
                icon={IconName.GLOBE}
                iconType={IconVariant.REGULAR}
              >
                {getViewersRestrictionLabel(pool.streamingCountries)}
              </CategorizationTag>
            </Popover>
          </div>

          <div className="mt-sm mb-lg text-grey-600 text-sm">{descriptionText}</div>
          {isWizard ? (
            <OttEndpointWizardStep row={rows[0]} />
          ) : (
            <>
              {showMetrics && (
                <OttEndpointFiltersSection
                  clearFilters={clearFilters}
                  isSearchOrFilterActive={isSearchOrFilterActive}
                  ottEndpoints={ottEndpoints}
                  rowStatusCount={rowStatusCount}
                  onStatusFilterChange={status =>
                    setOttEndpointFilterList(pool.uuid, { ...ottEndpointFilterList, status })
                  }
                />
              )}

              {showEmptySearch ? (
                <EmptySearchResult onClearFilters={clearFilters} />
              ) : (
                <div className="mt-lg space-y-sm">
                  {sortedRows.map(row => (
                    <div key={row.inputUuid} data-testid="ott-endpoint-input">
                      <div className="max-w-lg" />
                      <ExpandableContainer
                        expandContent={
                          <OttEndpointContent hasWriteAccess={hasWriteAccess} row={row} />
                        }
                        open={!!openInputs?.includes(row.inputUuid) || inputList?.length === 1}
                        setOpen={open => {
                          const uuidSet = new Set(openInputs ?? [])
                          if (open) {
                            uuidSet.add(row.inputUuid)
                          } else {
                            uuidSet.delete(row.inputUuid)
                          }
                          navigate({
                            search: prev => ({
                              ...prev,
                              openInputs: uuidSet.size > 0 ? Array.from(uuidSet) : undefined
                            })
                          })
                        }}
                      >
                        {!isWizard && <OttEndpointInputHeader row={row} />}
                      </ExpandableContainer>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Panel>
      </FormProvider>
      <ShareUrlModal />
      <AddOttEndpointModal />
    </PageContainer>
  )
}

export default OttEndpointList
